// Copyright (c) Docugami, Inc. All rights reserved.

import { ActionTileProps } from "components/homepage/ActionTiles";
import { themeBlue, themeRed, greyText } from "components/shell/presentation/AppStyles";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store";
import { selectIsLimitedOrganization, selectIsTrialSubscription } from 'store/AccountInfoHelpers';
import { useAccountView } from "./AccountView";
import { NavigationType, useNavigationService } from "./navigationService";
import { setAddDocsVisibility } from "store/display/displaySlice";

export enum ActionTileId {
    // Account expired info
    ExpiredInfo = 'Account_Expired',

    // Limits warning tiles
    PageLimitWarning = 'Upgrade_PageLimitWarning',
    ExpirationWarning = 'Upgrade_ExpiryWarning',
    UsersLimitWarning = 'Upgrade_UsersLimitWarning',

    // Document management tiles
    AddDocs = 'Upload_Docs',

    // Workpath tiles
    TabularReport = 'Create_TabularReport',
    Abstract = 'Create_Abstract',
    ExcelExport = 'Create_ExcelExport',
    DiligenceReport = 'Create_DiligenceReport',
    AutomationAnywhereDocumentAssembly = 'Create_AutomationAnywhereDocumentAssembly',
    AutomationAnywhereWorkFlow = 'Create_AutomationAnywhereWorkFlow',
    UiPathWorkFlow = 'Create_UiPathWorkFlow',
    UiPathDocumentAssembly = 'Create_UiPathDocumentAssembly'
}

export function useDocumentManagementTiles(): { [id: string]: ActionTileProps } {
    const dispatch = useDispatch();

    return (
        {
            [ActionTileId.AddDocs]: {
                id: ActionTileId.AddDocs,
                actionText: "Add Documents",
                iconInfo: { name: 'BulkUpload', height: 0.8, width: 1, defaultColor: themeBlue },
                onClick: () => dispatch(setAddDocsVisibility(true))
            }
        }
    )
}

export function useLimitsCtaTiles(): { [id: string]: ActionTileProps } {
    const isLimitedOrganization = useSelector(selectIsLimitedOrganization)
    const { daysLeftThresholdReached, pagesCount, maxPages, pageLimitThresholdReached } = useAccountView();
    const navService = useNavigationService();

    const limitsTiles: { [id: string]: ActionTileProps } = {};
    const isTrialOrg = useSelector((state: AppState) => selectIsTrialSubscription(state));

    if (isLimitedOrganization) {
        const expiredTileProps = {
            id: ActionTileId.ExpiredInfo,
            iconInfo: {
                name: 'Warning',
                height: .8,
                width: .8,
                defaultColor: themeRed
            },
            actionText: `Your ${isTrialOrg ? 'free trial' : 'subscription'} has expired. Click to upgrade.`,
            onClick: () => navService.navToAccountPage(NavigationType.PushNewPage)
        }

        limitsTiles[ActionTileId.ExpiredInfo] = expiredTileProps;

        // We do not show warning tiles when an org has expired, so return early
        return limitsTiles;
    }

    if (daysLeftThresholdReached) {
        const expirationWarningTileProps = {
            id: ActionTileId.ExpirationWarning,
            iconInfo: {
                name: 'Warning',
                height: .8,
                width: .8,
                defaultColor: greyText
            },
            actionText: `Your ${isTrialOrg ? 'free trial' : 'subscription'} is expiring soon. Click to upgrade.`,
            onClick: () => navService.navToAccountPage(NavigationType.PushNewPage)
        }

        limitsTiles[ActionTileId.ExpirationWarning] = expirationWarningTileProps;
    }

    if (pageLimitThresholdReached) {
        const pagesLimitWarningTileProps = {
            id: ActionTileId.PageLimitWarning,
            iconInfo: {
                name: 'ReportWarning',
                height: .8,
                width: .8,
                defaultColor: greyText
            },
            actionText: `You've used ${pagesCount} of ${maxPages} pages. Click to upgrade.`,
            onClick: () => navService.navToAccountPage(NavigationType.PushNewPage)
        }

        limitsTiles[ActionTileId.PageLimitWarning] = pagesLimitWarningTileProps;
    }

    return limitsTiles;
}
